import { useEffect, useRef, useState } from 'react';
import { useRender, useSlider, useT } from '../context';
import { SmartLink } from './common';

export interface HeroSliderProps {
  sliderId: string;
}

/**
 * Accessible replacement for the Revolution Slider of the legacy home page (no jQuery, no premium plugin):
 * - same geometry as the legacy config (grid heights 650/600/550/450, captions at 200/280/380 px of 650),
 * - 5 s autoplay, "parallax to top/bottom" transitions and the staggered caption fly-ins,
 * - arrows, pause/play button (WCAG 2.2.2), keyboard arrows, swipe, pause on hover/focus,
 * - no autoplay and no motion for prefers-reduced-motion, and optional mobile image variants.
 */
export function HeroSlider({ sliderId }: HeroSliderProps) {
  const slider = useSlider(sliderId);
  const { mode } = useRender();
  const t = useT();
  const [active, setActive] = useState(0);
  const [previous, setPrevious] = useState<number | null>(null);
  const [playing, setPlaying] = useState(true);
  const [hovered, setHovered] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(false);
  const touchX = useRef<number | null>(null);
  const slides = slider?.slides ?? [];
  const count = slides.length;

  useEffect(() => {
    const mq = window.matchMedia?.('(prefers-reduced-motion: reduce)');
    if (!mq) return;
    setReducedMotion(mq.matches);
    if (mq.matches) setPlaying(false);
    const onChange = () => setReducedMotion(mq.matches);
    mq.addEventListener?.('change', onChange);
    return () => mq.removeEventListener?.('change', onChange);
  }, []);

  const goTo = (i: number) => {
    if (count === 0) return;
    const next = (i + count) % count;
    if (next === active) return;
    setPrevious(active);
    setActive(next);
  };

  useEffect(() => {
    if (!playing || hovered || count < 2 || mode === 'editor') return;
    const timer = window.setTimeout(() => goTo(active + 1), slider?.autoplayMs ?? 5000);
    return () => window.clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, playing, hovered, count, mode, slider?.autoplayMs]);

  if (!slider || count === 0) return null;

  return (
    <section
      className={`rev_slider_wrapper kfz-hero${reducedMotion ? ' kfz-hero--static' : ''}`}
      aria-roledescription="carousel"
      aria-label={t('sliderLabel')}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onFocusCapture={() => setHovered(true)}
      onBlurCapture={() => setHovered(false)}
      onKeyDown={(e) => {
        if (e.key === 'ArrowLeft') goTo(active - 1);
        if (e.key === 'ArrowRight') goTo(active + 1);
      }}
      onTouchStart={(e) => (touchX.current = e.touches[0].clientX)}
      onTouchEnd={(e) => {
        if (touchX.current === null) return;
        const dx = e.changedTouches[0].clientX - touchX.current;
        if (Math.abs(dx) > 40) goTo(active + (dx < 0 ? 1 : -1));
        touchX.current = null;
      }}
    >
      <div className="rev_slider kfz-hero__viewport" aria-live={playing && !hovered ? 'off' : 'polite'}>
        <ul>
          {slides.map((slide, i) => {
            const state = i === active ? 'is-active' : i === previous ? 'is-leaving' : '';
            const button = slide.buttonLabel ? (
              <div className="slide-content-box kfz-cap kfz-cap--btn">
                <div className="button">
                  <SmartLink className="thm-btn" href={slide.buttonHref}>
                    {slide.buttonLabel} <i className="fa fa-caret-right" aria-hidden="true" />
                  </SmartLink>
                </div>
              </div>
            ) : null;
            return (
              <li
                key={slide.id}
                className={`kfz-hero__slide kfz-hero__slide--${slide.transition} ${state}`}
                aria-roledescription="slide"
                aria-label={t('slideOf', { n: i + 1, total: count })}
                aria-hidden={i === active ? undefined : true}
                {...(i !== active ? { inert: '' } : {})}
              >
                <picture className="kfz-hero__media">
                  {slide.mobile ? <source media="(max-width: 767px)" srcSet={slide.mobile.url} /> : null}
                  <img
                    src={slide.desktop.url}
                    alt={slide.alt}
                    width={slide.desktop.width ?? 1920}
                    height={slide.desktop.height ?? 650}
                    loading={i === 0 ? 'eager' : 'lazy'}
                    decoding={i === 0 ? 'sync' : 'async'}
                    {...(i === 0 ? { fetchpriority: 'high' } : {})}
                  />
                </picture>
                <div className="kfz-hero__overlay" aria-hidden="true" />
                <div className="container kfz-hero__captions">
                  {slide.subtitle ? (
                    <div className="slide-content-box kfz-cap kfz-cap--sub">
                      <p className="kfz-slide-sub">{slide.subtitle}</p>
                    </div>
                  ) : null}
                  {slide.title ? (
                    <div className="slide-content-box kfz-cap kfz-cap--title">
                      {i === 0 ? <h1 className="kfz-slide-title">{slide.title}</h1> : <p className="kfz-slide-title">{slide.title}</p>}
                    </div>
                  ) : null}
                  {button}
                </div>
              </li>
            );
          })}
        </ul>
        {slider.showArrows && count > 1 ? (
          <>
            <button type="button" className="tparrows kfz-hero__arrow kfz-hero__arrow--prev" aria-label={t('previousSlide')} onClick={() => goTo(active - 1)}>
              <i className="fa fa-angle-left" aria-hidden="true" />
            </button>
            <button type="button" className="tparrows kfz-hero__arrow kfz-hero__arrow--next" aria-label={t('nextSlide')} onClick={() => goTo(active + 1)}>
              <i className="fa fa-angle-right" aria-hidden="true" />
            </button>
          </>
        ) : null}
        {count > 1 ? (
          <button
            type="button"
            className="kfz-hero__toggle"
            aria-label={playing ? t('pauseSlider') : t('playSlider')}
            aria-pressed={!playing}
            onClick={() => setPlaying((p) => !p)}
          >
            <i className={`fa ${playing ? 'fa-pause' : 'fa-play'}`} aria-hidden="true" />
          </button>
        ) : null}
      </div>
    </section>
  );
}
